import React, { useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import useAdminAuth from '../hooks/useAdminAuth';

// Import admin pages
import AdminDashboard from '../pages/admin/Dashboard';
import GameEditor from '../pages/admin/GameEditor';
import UserMonitor from '../pages/admin/UserMonitor';
import StoryManager from '../pages/admin/StoryManager';
import UserActions from '../pages/admin/UserActions';

const AdminLayout = () => {
  const location = useLocation();
  const { adminEmail, logout, requireAuth, isAuthenticated } = useAdminAuth();

  // Check authentication on mount
  useEffect(() => {
    requireAuth();
  }, [requireAuth]);

  // Function to determine if a nav item is active
  const isActive = (path) => {
    // If path is dashboard, only match exact. For others, check if the current path starts with the given path
    if (path === '/admin/dashboard' || path === '/admin') {
      return location.pathname === path || location.pathname === '/admin' || location.pathname === '/admin/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  // If not authenticated, render nothing (will be redirected by requireAuth)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Navigation Bar */}
      <nav className="bg-indigo-700 text-white p-4 shadow-md">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Link to="/admin" className="text-xl font-bold">
              ハンターゲーム管理画面
            </Link>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm">
              ログイン中: {adminEmail}
            </span>
            <button
              onClick={logout}
              className="bg-indigo-800 hover:bg-indigo-900 px-3 py-1 rounded text-sm"
            >
              ログアウト
            </button>
          </div>
        </div>
      </nav>

      {/* Side Navigation and Main Content */}
      <div className="flex h-[calc(100vh-64px)]">
        <div className="w-64 bg-white shadow-lg p-4">
          <h2 className="font-bold text-lg text-gray-700 mb-4">
            管理パネル
          </h2>
          <ul className="space-y-2">
            <li>
              <Link
                to="/admin/dashboard"
                className={`block p-2 rounded ${
                  isActive('/admin/dashboard')
                    ? 'bg-indigo-100 text-indigo-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                ダッシュボード
              </Link>
            </li>
            <li>
              <Link
                to="/admin/game-editor"
                className={`block p-2 rounded ${
                  isActive('/admin/game-editor')
                    ? 'bg-indigo-100 text-indigo-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                ゲームエディタ
              </Link>
            </li>
            <li>
              <Link
                to="/admin/stories"
                className={`block p-2 rounded ${
                  isActive('/admin/stories')
                    ? 'bg-indigo-100 text-indigo-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                ストーリー管理
              </Link>
            </li>
            <li>
              <Link
                to="/admin/users"
                className={`block p-2 rounded ${
                  isActive('/admin/users')
                    ? 'bg-indigo-100 text-indigo-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                ユーザーモニター
              </Link>
            </li>
            <li>
              <Link
                to="/admin/user-actions"
                className={`block p-2 rounded ${
                  isActive('/admin/user-actions')
                    ? 'bg-indigo-100 text-indigo-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                ユーザーアクション
              </Link>
            </li>
          </ul>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          <Routes>
            <Route path="/" element={<AdminDashboard />} />
            <Route path="/dashboard" element={<AdminDashboard />} />
            <Route path="/game-editor" element={<GameEditor />} />
            <Route path="/game-editor/:storyId" element={<GameEditor />} />
            <Route path="/users" element={<UserMonitor />} />
            <Route path="/stories" element={<StoryManager />} />
            <Route path="/user-actions" element={<UserActions />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;